import {
  AlertTriangle,
  ArrowUp,
  Check,
  CloudUpload,
  FileSpreadsheet,
  FileText,
  HelpCircle,
  Image as ImageIcon,
  Info,
  Minus,
  RotateCcw,
  X,
} from 'lucide-react'
import React, {FC, ReactNode, RefObject, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'

import {fileExtension} from '../../utils/teacherEntry'
import TemplateDownloadLink from '../TemplateDownloadLink'
import {UPLOAD_STEPS, useGradualUploadProgress} from './useGradualUploadProgress'

/**
 * The page the uploader is on. Passed explicitly (12 Oct review, point 3): it picks the
 * page-specific labels; the interface, limits, progress, feedback and retries are the same.
 */
export type UploaderContext = 'teachers' | 'allocation'

/** Where one file stands: the circle at the start of its row. */
export type UploaderFileState =
  | 'done'
  /** Read, but some rows were left out: usable, with a warning and a way to fix it. */
  | 'warning'
  | 'busy'
  | 'upload'
  | 'error'
  | 'question'
  | 'neutral'

/** One file in the status list. */
export interface UploaderFile {
  id: string
  name: string
  /** The file itself, for the type tile (sheet / PDF / image / document). */
  file: File
  state: UploaderFileState
  /** The status line: "Ready for review · 18 names", "Uploading… 65%", the failure reason. */
  text: string
  /** 0..1 while its pieces are sent (`state: 'upload'`). */
  progress?: number | null
  /** A muted line under the status ("A photo or scan takes 30 to 50 seconds…"). */
  hint?: string | null
  /** Under the status: e.g. the rows the file left out, with why. */
  details?: ReactNode
  /** Page-specific buttons on the row (Answer, Map columns, View details). */
  actions?: ReactNode
  /** Offer "Try again" on the row (a failed file). */
  retryable?: boolean
  /**
   * Offer "Upload a fixed file" on the row: this file is taken off and the picker opens, so a
   * corrected copy replaces it (a partly read or damaged file).
   */
  replaceable?: boolean
  /** The replace button's label ("Replace file" for a failed file); "Upload a fixed file" else. */
  replaceLabel?: string
  /** No X: it goes with another row's file, or it is still being sent. */
  locked?: boolean
}

/** The status panel's headline: the files as a whole. */
export interface UploaderHead {
  /** Colours the panel and its icon: reading, a question, all read, some failed, none read. */
  tone: 'busy' | 'question' | 'ready' | 'partial' | 'failed' | 'empty'
  kicker?: string | null
  title: string
  detail?: string | null
  /** Something still running after the files were read (shown on its own line, with a spinner). */
  pending?: string | null
  /** A step for every file at once (Teacher Allocation's Import), under the headline's text. */
  action?: ReactNode
}

/** A file refused when it was added, with the reason. */
export interface UploaderRejectedFile {
  name: string
  text: string
}

interface Props {
  context: UploaderContext
  /** The files on the uploader. With none, the initial dropzone shows. */
  files: UploaderFile[]
  /** Headline over the files; the panel shows no headline without one. */
  head?: UploaderHead | null
  /** Files dropped or picked. Dropping starts processing; browse opens the picker directly. */
  onFiles: (files: File[]) => void
  onRemove: (id: string) => void
  onRetry?: (id: string) => void
  /** "Upload a fixed file": the row's file is removed, then the picker opens. */
  onReplace?: (id: string) => void
  /** Retries and removes wait while something is in flight. */
  busy?: boolean
  /** No new files now (another read is being prepared): the drop areas are dimmed. */
  disabled?: boolean
  accept: string
  /** One file at a time: a new one replaces it ("Drop a different file here"). */
  singleFile?: boolean
  /** "Excel, CSV, PDF, Word or images" and "Up to 10 files · 10 MB each · 50 MB total". */
  formatsText: string
  limitsText: string
  onDownloadTemplate: () => void
  templateBusy?: boolean
  rejected?: UploaderRejectedFile[]
  onDismissRejected?: () => void
  /** Page-specific guidance under the uploader (scan note, photo tips). */
  guidance?: ReactNode
  /** Inside the status panel, under the files: page-specific summaries and reports. */
  children?: ReactNode
  /** The status panel, for the page to bring into view. */
  panelRef?: RefObject<HTMLDivElement | null>
}

const TYPE_ICONS = {
  sheet: FileSpreadsheet,
  image: ImageIcon,
  pdf: FileText,
  doc: FileText,
} as const

const SPREADSHEETS = new Set(['xlsx', 'xls', 'csv', 'ods', 'txt'])
const IMAGES = new Set(['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif', 'gif', 'bmp', 'tif', 'tiff'])

/** Colour of a file's type tile: sheets green, PDFs red, images purple, documents blue. */
export const uploaderFileTone = (file: File): keyof typeof TYPE_ICONS => {
  const ext = fileExtension(file.name)
  if (SPREADSHEETS.has(ext)) return 'sheet'
  if (IMAGES.has(ext) || file.type.startsWith('image/')) return 'image'
  return ext === 'pdf' ? 'pdf' : 'doc'
}

/** The ring spinner of the approved layouts (a blue arc on a faint ring). */
const Ring: FC<{size: 'sm' | 'lg'}> = ({size}) => (
  <span className={`step4-te-ring step4-te-ring--${size}`} aria-hidden />
)

const STATE_ICONS: Record<UploaderFileState, ReactNode> = {
  done: <Check size={15} strokeWidth={3} aria-hidden />,
  warning: <AlertTriangle size={14} strokeWidth={2.5} aria-hidden />,
  busy: <Ring size='sm' />,
  upload: <ArrowUp size={15} strokeWidth={2.5} aria-hidden />,
  error: <AlertTriangle size={15} aria-hidden />,
  question: <HelpCircle size={16} aria-hidden />,
  neutral: <Minus size={15} aria-hidden />,
}

const HEAD_ICONS: Record<UploaderHead['tone'], ReactNode> = {
  busy: <Ring size='lg' />,
  question: <HelpCircle size={30} aria-hidden />,
  ready: <Check size={26} strokeWidth={3} aria-hidden />,
  partial: <AlertTriangle size={26} aria-hidden />,
  failed: <AlertTriangle size={26} aria-hidden />,
  empty: <Info size={26} aria-hidden />,
}

/**
 * The one onboarding uploader, used by Add Teachers and Teacher Allocation alike (12 Oct review,
 * point 3; layouts of the 11 Oct review, point 5): drag and drop or browse, the formats and
 * limits, the optional template with its tooltip, then — once files are added — a status panel
 * with the overall state, a row per file (state, type, measured upload progress or a reading
 * spinner, the result or the reason it failed, "Try again"), and a compact area to add more.
 * Refused files are listed with their reason; accepted ones are never discarded by them.
 *
 * It only shows: each page reads the files its own way (`onFiles`) and says where they stand
 * (`files`, `head`), so the two pages differ in labels, guidance and processing, not in how the
 * uploader looks or behaves.
 */
const OnboardingUploader: FC<Props> = ({
  context,
  files,
  head,
  onFiles,
  onRemove,
  onRetry,
  onReplace,
  busy = false,
  disabled = false,
  accept,
  singleFile = false,
  formatsText,
  limitsText,
  onDownloadTemplate,
  templateBusy,
  rejected = [],
  onDismissRejected,
  guidance,
  children,
  panelRef,
}) => {
  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const ownPanelRef = useRef<HTMLDivElement>(null)
  const statusRef = panelRef ?? ownPanelRef
  const [isDragging, setIsDragging] = useState(false)
  /** Bumped when files are added: brings the status panel into view on small screens. */
  const [revealTick, setRevealTick] = useState(0)
  const t = (id: string, values?: Record<string, ReactNode>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.${id}`}, values as any)

  // Upload bars move in steps with the transfer — 0%, 10%, 40%, 60%, 100% — and a row moves on to
  // "Reading…" only once 100% was shown. A new upload starts at the first step.
  const gradual = useGradualUploadProgress(files)
  const shownFiles = files.map((item): UploaderFile => {
    const fraction = gradual[item.id] ?? (item.state === 'upload' ? UPLOAD_STEPS[0] : undefined)
    if (fraction === undefined) return item
    return {
      ...item,
      state: 'upload',
      progress: fraction,
      text: t('FILE_STATUS_UPLOADING', {percent: Math.round(fraction * 100)}),
      hint: null,
      retryable: false,
      replaceable: false,
      actions: null,
    }
  })

  const take = (list: File[]) => {
    if (disabled || list.length === 0) return
    onFiles(list)
    setRevealTick((tick) => tick + 1)
  }
  const openPicker = () => {
    if (!disabled) inputRef.current?.click()
  }

  // After a drop or pick, the status panel may sit below the fold (small screens): scroll it
  // into view, only when it is off-screen. Feedback stays on the page, never a blocking modal.
  useEffect(() => {
    if (revealTick === 0) return
    const panel = statusRef.current
    if (!panel) return
    const {top, bottom} = panel.getBoundingClientRect()
    if (top < 0 || bottom > window.innerHeight) {
      panel.scrollIntoView({behavior: 'smooth', block: 'nearest'})
    }
  }, [revealTick, statusRef])

  const dragHandlers = {
    onDrop: (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      take(Array.from(e.dataTransfer.files ?? []))
    },
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!isDragging && !disabled) setIsDragging(true)
    },
    onDragEnter: (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!isDragging && !disabled) setIsDragging(true)
    },
    onDragLeave: (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      // Moving onto a child of the drop area is not leaving it.
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
      setIsDragging(false)
    },
  }
  const keyOpens = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      openPicker()
    }
  }

  const renderRow = (item: UploaderFile) => {
    const tone = uploaderFileTone(item.file)
    const TypeIcon = TYPE_ICONS[tone]
    const percent = Math.round((item.progress ?? 0) * 100)
    return (
      <li key={item.id} className={`step4-te-status-file step4-te-status-file--${item.state}`}>
        <span className={`step4-te-state step4-te-state--${item.state}`}>
          {STATE_ICONS[item.state]}
        </span>
        <span className={`step4-te-type step4-te-type--${tone}`}>
          <TypeIcon size={18} aria-hidden />
        </span>
        <span className='step4-te-file-body'>
          <span className='step4-te-file-name' title={item.name}>
            {item.name}
          </span>
          {item.text && (
            <span className={`step4-te-file-line step4-te-file-line--${item.state}`}>
              {item.text}
            </span>
          )}
          {item.state === 'upload' && item.progress != null && (
            <span
              className='step4-te-file-progress'
              role='progressbar'
              aria-label={item.name}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
            >
              <span style={{width: `${percent}%`}} />
            </span>
          )}
          {item.hint && <span className='step4-te-file-hint'>{item.hint}</span>}
          {item.details}
          {(item.retryable && onRetry) || (item.replaceable && onReplace) || item.actions ? (
            <span className='step4-te-file-actions'>
              {item.replaceable && onReplace && (
                <button
                  type='button'
                  className='step4-te-file-action'
                  disabled={busy || disabled}
                  onClick={() => {
                    onReplace(item.id)
                    openPicker()
                  }}
                >
                  <CloudUpload size={13} aria-hidden />
                  {item.replaceLabel ?? t('UPLOAD_FIXED_FILE')}
                </button>
              )}
              {item.retryable && onRetry && (
                <button
                  type='button'
                  className='step4-te-file-action'
                  onClick={() => onRetry(item.id)}
                  disabled={busy}
                >
                  <RotateCcw size={13} aria-hidden />
                  {t('RETRY')}
                </button>
              )}
              {item.actions}
            </span>
          ) : null}
        </span>
        {!item.locked && (
          <button
            type='button'
            className='step4-te-file-remove'
            onClick={() => onRemove(item.id)}
            aria-label={t('REMOVE_FILE')}
            title={t('REMOVE_FILE')}
          >
            <X size={16} aria-hidden />
          </button>
        )}
      </li>
    )
  }

  return (
    <div className={`onboarding-uploader onboarding-uploader--${context}`}>
      <input
        ref={inputRef}
        type='file'
        multiple={!singleFile}
        accept={accept}
        className='d-none'
        onChange={(e) => {
          const list = Array.from(e.target.files ?? [])
          e.target.value = ''
          take(list)
        }}
      />

      {files.length === 0 ? (
        // Initial: one dropzone with the formats and limits, the picker and the template.
        <div
          role='button'
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || undefined}
          className={`step4-te-dropzone${isDragging ? ' step4-te-dropzone--active' : ''}${
            disabled ? ' step4-te-dropzone--disabled' : ''
          }`}
          onClick={openPicker}
          onKeyDown={keyOpens}
          {...dragHandlers}
        >
          <span className='step4-te-dropzone-icon'>
            <CloudUpload size={26} aria-hidden />
          </span>
          <p className='step4-te-dropzone-title'>
            {t(singleFile ? 'DROPZONE_TITLE' : 'DROPZONE_TITLE_FILES')}
          </p>
          <p className='step4-te-dropzone-formats'>{formatsText}</p>
          {limitsText && <p className='step4-te-dropzone-formats'>{limitsText}</p>}
          <button
            type='button'
            className='step4-te-choose-btn'
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation()
              openPicker()
            }}
          >
            <CloudUpload size={16} aria-hidden />
            {t(singleFile ? 'CHOOSE_FILES' : 'CHOOSE_FILES_MULTI')}
          </button>
          <TemplateDownloadLink
            className='step4-te-template-link'
            onDownload={onDownloadTemplate}
            busy={templateBusy}
          />
        </div>
      ) : (
        // Processing / ready: overall state first, then each file, then a compact area to add
        // more. The whole panel takes a drop, so a file can land anywhere on it.
        <div
          ref={statusRef}
          className={`step4-te-status step4-te-status--${head?.tone ?? 'busy'}`}
          {...dragHandlers}
        >
          {head && (
            <div className='step4-te-status-head' aria-live='polite'>
              <span className='step4-te-status-icon'>{HEAD_ICONS[head.tone]}</span>
              <div className='step4-te-status-text'>
                {head.kicker && <p className='step4-te-status-kicker'>{head.kicker}</p>}
                <p className='step4-te-status-title'>{head.title}</p>
                {head.detail && <p className='step4-te-status-detail'>{head.detail}</p>}
                {head.pending && (
                  <p className='step4-te-status-detail step4-te-status-pending' role='status'>
                    <span
                      className='spinner-border spinner-border-sm text-primary'
                      aria-hidden='true'
                    />
                    <span>{head.pending}</span>
                  </p>
                )}
                {head.action && <div className='step4-te-status-action'>{head.action}</div>}
              </div>
            </div>
          )}
          <ul className='step4-te-status-files' aria-live='polite'>
            {shownFiles.map(renderRow)}
          </ul>
          {children}
          <div
            role='button'
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            className={`step4-te-dropmore${isDragging ? ' step4-te-dropmore--active' : ''}${
              disabled ? ' step4-te-dropmore--disabled' : ''
            }`}
            onClick={openPicker}
            onKeyDown={keyOpens}
          >
            <p className='step4-te-dropmore-title'>
              {intl.formatMessage(
                {
                  id: singleFile
                    ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DROP_REPLACE'
                    : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DROP_MORE',
                },
                {
                  browse: (chunks: ReactNode) => (
                    <span className='step4-te-dropmore-link'>{chunks}</span>
                  ),
                }
              )}
            </p>
            <p className='step4-te-dropmore-limits'>{formatsText}</p>
            {limitsText && <p className='step4-te-dropmore-limits'>{limitsText}</p>}
          </div>
        </div>
      )}

      {rejected.length > 0 && (
        <div className='step4-te-rejected' role='alert'>
          <AlertTriangle size={16} aria-hidden className='step4-te-rejected-icon' />
          <div className='step4-te-rejected-body'>
            <p className='step4-te-rejected-title'>{t('REJECTED_TITLE', {count: rejected.length})}</p>
            <ul>
              {rejected.map((file, index) => (
                <li key={`${file.name}-${index}`}>
                  <strong>{file.name}</strong> · {file.text}
                </li>
              ))}
            </ul>
          </div>
          {onDismissRejected && (
            <button
              type='button'
              className='step4-te-file-remove'
              onClick={onDismissRejected}
              aria-label={t('REJECTED_DISMISS')}
              title={t('REJECTED_DISMISS')}
            >
              <X size={16} aria-hidden />
            </button>
          )}
        </div>
      )}

      {guidance}
    </div>
  )
}

export default OnboardingUploader
